/**
 * The product's documentation: every guide this person may read, and one open.
 *
 * The docs are the API's (`app/modules/docs/content/*.md`), and so is the choice
 * of which to list: each is gated on a permission — the one its subject needs —
 * and through it on the Service Switch, so a guide to a service the installation
 * lacks, or to screens this person cannot open, is simply not here. Adding a doc
 * is adding a Markdown file to the API; nothing in this screen changes.
 *
 * Rendered by the shared `DocumentText` in its `guide` dialect: React elements
 * built from parsed data, never HTML from the document.
 */

import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen } from 'lucide-react';
import {
  ApiError,
  Card,
  CardBody,
  DocumentText,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  api,
  cn,
  formatDate,
} from '@connect/web-shared';

export interface DocSummary {
  slug: string;
  title: string;
  summary: string;
  section: string;
  order: number;
  providers: string[];
  updated: string | null;
}

interface Doc extends DocSummary {
  body: string;
}

export const docsKeys = {
  index: ['docs'] as const,
  doc: (slug: string) => ['docs', slug] as const,
};

/** Where one doc opens — for a link from anywhere else in the portal. */
export function docPath(slug: string): string {
  return `/docs/${encodeURIComponent(slug)}`;
}

export function DocsPage() {
  const { slug } = useParams<{ slug?: string }>();
  const index = useQuery({
    queryKey: docsKeys.index,
    queryFn: () => api.get<DocSummary[]>('/docs'),
  });
  const docs = index.data ?? [];
  const open = slug ?? docs[0]?.slug;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Docs"
        description="Guides to setting up and running what this installation offers."
        breadcrumbs={[{ label: 'Admin' }, { label: 'Docs' }]}
      />

      {index.isLoading ? (
        <LoadingState label="Loading the docs" />
      ) : index.error ? (
        <ErrorState
          message={index.error instanceof ApiError ? index.error.message : 'The docs could not be loaded.'}
          onRetry={() => void index.refetch()}
        />
      ) : docs.length === 0 ? (
        <EmptyState
          icon={<BookOpen className="size-5" />}
          title="No docs for you here"
          description="Guides appear for the services this installation has and the screens you can open."
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <DocsIndex docs={docs} open={open} />
          {open ? <DocArticle slug={open} /> : null}
        </div>
      )}
    </div>
  );
}

function DocsIndex({ docs, open }: { docs: DocSummary[]; open: string | undefined }) {
  const sections = new Map<string, DocSummary[]>();
  for (const doc of docs) sections.set(doc.section, [...(sections.get(doc.section) ?? []), doc]);
  return (
    <nav aria-label="Docs" className="space-y-4 lg:sticky lg:top-4 lg:self-start">
      {[...sections].map(([section, items]) => (
        <div key={section} className="space-y-1">
          <h2 className="px-2 text-2xs font-semibold uppercase tracking-wide text-faint">{section}</h2>
          <ul className="space-y-0.5">
            {items.map((doc) => (
              <li key={doc.slug}>
                <Link
                  to={docPath(doc.slug)}
                  aria-current={doc.slug === open ? 'page' : undefined}
                  className={cn(
                    'block rounded-control px-2 py-1.5 text-sm transition-colors',
                    doc.slug === open
                      ? 'bg-accent-subtle font-medium text-accent'
                      : 'text-body hover:bg-surface-2',
                  )}
                >
                  {doc.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function DocArticle({ slug }: { slug: string }) {
  const doc = useQuery({
    queryKey: docsKeys.doc(slug),
    queryFn: () => api.get<Doc>(`/docs/${encodeURIComponent(slug)}`),
  });

  if (doc.isLoading) return <LoadingState label="Opening the guide" />;
  if (doc.error || !doc.data) {
    return (
      <ErrorState
        message={doc.error instanceof ApiError ? doc.error.message : 'The guide could not be opened.'}
        onRetry={() => void doc.refetch()}
      />
    );
  }
  return (
    <Card>
      <CardBody>
        <article className="mx-auto max-w-3xl space-y-4">
          <header className="space-y-1 border-b border-line pb-4">
            <h2 className="text-lg font-semibold text-heading">{doc.data.title}</h2>
            <p className="text-sm text-muted">{doc.data.summary}</p>
            {doc.data.updated ? (
              <p className="text-2xs text-faint">Updated {formatDate(doc.data.updated)}</p>
            ) : null}
          </header>
          <DocumentText content={doc.data.body} dialect="guide" />
        </article>
      </CardBody>
    </Card>
  );
}
